import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

import Button from "../Button";
import AppDownload from "../AppDownload";
import ModalProvider, { ModalContext } from "../ModalProvider";
import GoogleAds from "../GoogleAds";

import * as Constants from "../../lib/constants";
import * as styles from "./Layout.module.css";

const ButtonDownload = () => {
  const { openModal } = useContext(ModalContext);
  return (
    <Button
      label="Download"
      name="download"
      onClick={() => {
        openModal({
          content: <AppDownload />,
        });
      }}
    />
  );
};

const Layout = ({
  children,
  contentContainerStyle,
  title,
  metaDesc,
  iosID,
  withSideAds,
  ogImage,
  hreflangArr,
  langUser,
  ogUrlPath,
  canonicalPath,
}) => {
  const [isWebview, setIsWebview] = useState(false);
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paramAppWebview = queryParams.get("app-webview");
    setIsWebview(paramAppWebview === "true");
  }, []);

  const fontURL = "https://fonts.googleapis.com/css?family=Roboto:400,600&display=swap";

  const pageTitle = `Tobo | ${title || "Learn most common words, verbs and adjectives"}`;

  const pageDescription =
    metaDesc ||
    "Fun, engaging way of learning vocabulary. Interact with the flashcards. Learn articles, plurals and pronunciation of most common words.";

  const childrenContainerStyle = isWebview
    ? {
        ...contentContainerStyle,
        marginTop: 0,
      }
    : contentContainerStyle;

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: langUser ? langUser.split("-")[0] : "en",
        }}
      >
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link rel="preload" as="style" href={fontURL} />
        <link rel="stylesheet" href={fontURL} media="print" onload="this.media='all'" />
        <title>{pageTitle}</title>
        {(hreflangArr || []).map((item) => (
          <link
            key={item.langKey}
            rel="alternate"
            href={`https://www.toboapp.com${item.fullPath}`}
            hreflang={item.langKey}
          />
        ))}
        <meta name="og:title" property="og:title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="og:description" property="og:description" content={pageDescription} />
        {iosID && <meta name="apple-itunes-app" content={`app-id=${iosID}`} />}
        {ogUrlPath !== undefined && (
          <meta name="og:url" property="og:url" content={`https://www.toboapp.com${ogUrlPath}`} />
        )}
        <meta property="og:image" content={ogImage || "https://www.toboapp.com/og-image.png"} />
        {canonicalPath !== undefined && (
          <link rel="canonical" href={`https://www.toboapp.com${canonicalPath}`} />
        )}
      </Helmet>
      <ModalProvider>
        {!isWebview && (
          <nav className={styles.navbar}>
            <div className={styles.navbarInnerContainer}>
              <Link to="/">
                <StaticImage
                  src="../../images/logo.svg"
                  alt="Tobo App Logo"
                  height={24}
                  width={93}
                />
              </Link>
              <div className={styles.socialLinksContainer}>
                <a rel="noreferrer" target="_blank" href={Constants.YOUTUBE_URL}>
                  <StaticImage
                    src="../../images/youtube.png"
                    alt="youtube"
                    height={24}
                    width={34}
                  />
                </a>
                <a rel="noreferrer" target="_blank" href={Constants.FACEBOOK_URL}>
                  <StaticImage
                    src="../../images/facebook-colored.png"
                    alt="facebook"
                    height={24}
                    width={24}
                  />
                </a>
                <a rel="noreferrer" target="_blank" href={Constants.INSTAGRAM_URL}>
                  <StaticImage
                    src="../../images/instagram-colored.png"
                    alt="instagram"
                    height={24}
                    width={24}
                  />
                </a>
                <a rel="noreferrer" target="_blank" href={Constants.TWITTER_URL}>
                  <StaticImage
                    src="../../images/twitter-colored.png"
                    alt="twitter"
                    height={24}
                    width={24}
                  />
                </a>
                <ButtonDownload />
              </div>
            </div>
          </nav>
        )}
        {withSideAds ? (
          <div style={{ display: "flex" }}>
            <div className={styles.side}>
              <GoogleAds slot="5696660872" adFormat="auto" fullWidthResponsive="true" />
            </div>
            <div className={styles.childrenContainer} style={childrenContainerStyle}>
              {children}
            </div>
            <div className={styles.side}>
              <GoogleAds slot="5696660872" adFormat="auto" fullWidthResponsive="true" />
            </div>
          </div>
        ) : (
          <div className={styles.childrenContainer} style={childrenContainerStyle}>
            {children}
          </div>
        )}
      </ModalProvider>
    </>
  );
};

export default Layout;
